import React, { useState } from 'react';
import type { FC } from 'react';
import { match, partial } from 'variant';
import type { SelectOption } from '@wix/cli-ui-kit';
import { Link, SelectInput, Spinner, Text, useAsync } from '@wix/cli-ui-kit';
import type {
  AppDependencies,
  RequiredApp,
  BackOfficePage,
  BackOfficeExtensionContainer,
} from '@wix/extensions-data-client';
import {
  getEcomRequiredApps,
  useExtensionDataClient,
} from '@wix/extensions-data-client';
import { DashboardPluginWizard } from './dashboard-plugin/DashboardPluginWizard.js';
import { DashboardMenuPluginWizard } from './dashboad-menu-plugin/DashboardMenuPluginWizard.js';
import type { GenerateFlow } from '../generateFlows.js';
import { useWixCliAstroBi } from '../../../bi/index.js';

export type SlotsApp = AppDependencies & {
  appName: string;
  backofficePages: BackOfficePage[];
};

export const DashboardPlugin: FC<{ flow: GenerateFlow }> = ({ flow }) => {
  const bi = useWixCliAstroBi();
  const {
    getBackofficePages,
    getBackofficeExtensionWidgets,
    getBackofficeMenuItems,
  } = useExtensionDataClient();
  const [slotsApp, setSlotsApp] = useState<SlotsApp>();

  const { status } = useAsync(async () => {
    const [ecomRequiredApps, backofficePages, backofficeExtensionContainers] =
      await Promise.all([
        getEcomRequiredApps(),
        getBackofficePages(),
        match(
          flow,
          partial({
            DashboardPlugin: () => getBackofficeExtensionWidgets(),
            DashboardMenuPlugin: () => getBackofficeMenuItems(),
            default: () => [],
          })
        ),
      ]);

    const pluginsMap = backofficeExtensionContainers.reduce<
      Record<string, BackOfficeExtensionContainer>
    >((extensionContainersMap, backofficeExtensionContainer) => {
      extensionContainersMap[
        backofficeExtensionContainer.component.componentId
      ] = backofficeExtensionContainer;
      return extensionContainersMap;
    }, {});

    const supportedAppsMap = backofficePages.reduce<
      Record<string, SelectOption<SlotsApp>>
    >((acc, backofficePage) => {
      const shouldAddBackofficePage =
        backofficePage.component.data.backOfficePage.slotIds.some(
          (slotId) => !!pluginsMap[slotId]
        );

      if (shouldAddBackofficePage) {
        const requiredApps: [RequiredApp, ...RequiredApp[]] =
          ecomRequiredApps.appId === backofficePage.appId
            ? ecomRequiredApps.requiredApps
            : [
                {
                  appId: backofficePage.appId,
                  appName: backofficePage.appName,
                },
              ];

        acc[backofficePage.appId] ??= {
          key: backofficePage.appId,
          title: backofficePage.appName,
          value: {
            requiredApps,
            backofficePages: [],
            appName: backofficePage.appName,
            appId: backofficePage.appId,
          },
        };

        acc[backofficePage.appId]?.value.backofficePages.push(backofficePage);
      }

      return acc;
    }, {});

    const supportedApps = Object.values(supportedAppsMap).sort((a, b) =>
      a.title.localeCompare(b.title)
    );

    return {
      supportedApps,
      pluginsMap,
    };
  }, []);

  return match(status, {
    Error: () => null,
    Loading: () => <Spinner text={'Loading...'} />,
    Success: ({ result: { supportedApps, pluginsMap } }) => (
      <>
        <SelectInput<SlotsApp>
          label={'Select a Wix app to view its plugin slots'}
          // https://dev.wix.com/docs/wix-cli/guides/extensions/dashboard-extensions/dashboard-plugins/add-dashboard-plugin-extensions
          info={
            <Text>
              (<Link url="https://wix.to/T9OFciS">read more</Link>)
            </Text>
          }
          options={supportedApps}
          onSubmit={(option) => {
            bi.cliFlowStepAnswered({
              question: 'Select a Wix app to view its plugin slots',
              questionKey: 'generate_command.dashboard.plugin_app',
              answer: option.title,
              additionalInfo: {
                appId: option.value.appId,
                appName: option.value.appName,
              },
            });
            setSlotsApp(option.value);
          }}
        />
        {slotsApp &&
          match(
            flow,
            partial({
              DashboardPlugin: () => (
                <DashboardPluginWizard
                  slotsApp={slotsApp}
                  pluginsMap={Object.fromEntries(
                    Object.entries(pluginsMap).filter(
                      ([, plugin]) => plugin.appId === slotsApp.appId
                    )
                  )}
                />
              ),
              DashboardMenuPlugin: () => (
                <DashboardMenuPluginWizard
                  slotsApp={slotsApp}
                  pluginsMap={Object.fromEntries(
                    Object.entries(pluginsMap).filter(
                      ([, plugin]) => plugin.appId === slotsApp.appId
                    )
                  )}
                />
              ),
              default: () => null,
            })
          )}
      </>
    ),
  });
};
